import React, { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import styles from './styles.module.scss';

// assets

// services
import ErrorService from '../../../../../services/general/error';

// props
import { IPaginationResourceShort } from '../../../../../props/general';

// components
import { CommandBar, IColumn, ICommandBarItemProps, PrimaryButton, SearchBox, SelectionMode, ShimmeredDetailsList, Stack } from '@fluentui/react';
import { useStore } from '../../../../../stores/root';
import { faXmarkCircle } from '@fortawesome/pro-light-svg-icons';
import Text from './../../../../typography/text';
import GeneralService from '../../../../../services/general';
import { FilterOptionProps } from '../../../../uiframeworks/filters/panel';
import SelectedFilter from '../../../../uiframeworks/filters/selected';
import Pagination from '../../../../uiframeworks/pagination';
import GlobalConfig from '../../../../../config';
import PermissionsService from '../../../../../services/permissions';
import moment from 'moment';
import Tag from '../../../../uiframeworks/tag';
import { IEmployeePayslipResourceShortProps } from '../../../../../props/employees/payslips/payslip';
import EmployeePayslipService from '../../../../../services/employees/payslip';

type EmployeePayslipsListProps = {
    qs?: string[];
    payslips?: IEmployeePayslipResourceShortProps[];
    columns?: TEmployeePayslipColumn[];
    hideSearch?: boolean;
    hideCommandBar?: boolean;
    hideCreatePayslip?: boolean;
    hideFilter?: boolean;
    variant?: 'card' | 'plain';
}

export type TEmployeePayslipColumn = 'id' | 'actions' | 'employee' | 'contract' | 'payslipMonth' | 'amount' | 'status';

const EmployeePayslipsList: React.FC<EmployeePayslipsListProps> = (props: EmployeePayslipsListProps) => {
    const shownColumns: TEmployeePayslipColumn[] = props.columns || [
        'id',
        'actions',
        'employee',
        'contract',
        'payslipMonth',
        'amount',
        'status'
    ];

    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [showingMore, setShowingMore] = React.useState<boolean>(false);
    const [payslips, setPayslips] = React.useState<IEmployeePayslipResourceShortProps[]>(props.payslips || []);
    const [activePayslip, setActivePayslip] = React.useState<IEmployeePayslipResourceShortProps | undefined>();
    const [pagination, setPagination] = React.useState<IPaginationResourceShort | undefined>();
    const [selectedFilter, setSelectedFilter] = React.useState<FilterOptionProps[]>([]);
    const [keyword, setKeyword] = React.useState<string>("");
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();

    const columns: IColumn[] = [
        {
            key: "id",
            name: "Payslip #",
            fieldName: "id",
            minWidth: 250,
            maxWidth: 250,
            onRender: (item: IEmployeePayslipResourceShortProps) => {
                return <Stack styles={{ root: { padding: '2px 0px' } }}>
                    <NavLink to={`/hr/payslips/${item.id}`}><Text size={'small'}>#{item.id}</Text></NavLink>
                </Stack>
            }
        },
        {
            key: "employee",
            name: "Employee",
            fieldName: "employee",
            minWidth: 200,
            onRender: (item: IEmployeePayslipResourceShortProps) => {
                return <Stack styles={{ root: { padding: '2px 0px' } }}>
                    <NavLink to={`/users/administrators/${item.employee.id}`}>
                        <Text size={'small'}>{item.employee.name}</Text>
                    </NavLink>
                </Stack>
            }
        },
        {
            key: "contract",
            name: "Contract #",
            fieldName: "id",
            minWidth: 250,
            maxWidth: 250,
            onRender: (item: IEmployeePayslipResourceShortProps) => {
                return <Stack styles={{ root: { padding: '2px 0px' } }}>
                    {item.contract ? <NavLink to={`/employees/contracts/${item.id}`}><Text size={'small'}>#{item.id}</Text></NavLink> : null}
                    {!item.contract ? <Text size={'small'}>-</Text> : null}
                </Stack>
            }
        },
        {
            key: "month",
            name: "Month / Year",
            minWidth: 125,
            maxWidth: 125,
            isMultiline: true,
            onRender: (item: IEmployeePayslipResourceShortProps) => {
                return <Stack styles={{ root: { padding: '2px 0px' } }}>
                    <Text size={'small'}>{moment(item.payslipMonth, 'MM/YYYY').format("MMMM YYYY")}</Text>
                </Stack>
            }
        },
        {
            key: "amount",
            name: "Total Amount",
            minWidth: 225,
            isMultiline: true,
            onRender: (item: IEmployeePayslipResourceShortProps) => {
                return <Stack styles={{ root: { padding: '2px 0px' } }}>
                    <Text size={'small'}>Rp. {GeneralService.getNumberWithSeparator(item.totalAmount)}</Text>
                </Stack>
            }
        },
        {
            key: "status",
            name: "Status",
            fieldName: "status",
            minWidth: 100,
            maxWidth: 150,
            isMultiline: true,
            onRender: (item: IEmployeePayslipResourceShortProps) => {
                return <Stack horizontal>
                    {Number(item.unpaidAmount) > 0 ? <Tag variant={'error'} size={'small'} text={'Unpaid'} /> : null}
                    {Number(item.unpaidAmount) <= 0 ? <Tag variant={'success'} size={'small'} text={'Paid'} /> : null}
                </Stack>
            }
        }
    ];

    useEffect(() => {
        if (!props.payslips) {
            _onRetrievePayslips();
        } else {
            setLoaded(true);
        }
    }, [keyword]);

    const getQueryString = (pageNumber?: number) => {
        const qs: string[] = props.qs || [];
        qs.push(`top=${GlobalConfig.defaultTop}`);
        if (pageNumber) { qs.push(`page=${pageNumber}`); }
        if (keyword && keyword.trim() !== "") { qs.push(`search=${keyword}`) }

        return qs.join("&");
    }

    const _onRetrievePayslips = async (pageNumber?: number) => {
        try {
            setLoaded(false);
            const qs = getQueryString(pageNumber);
            const results = await EmployeePayslipService.retrieve(qs);

            setPayslips(results.data);
            setPagination(results.pagination);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'retrieve_orders_list_error',
                text: 'Failed to retrieve orders list. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    const _onKeywordChanged = (value?: string) => {
        setLoaded(true);
        setKeyword(value || "");
    }

    const getCommandBarItems = () => {
        let items: ICommandBarItemProps[] = [];
        let farItems: ICommandBarItemProps[] = [];

        if (!props.hideSearch) {
            items.push({
                key: "search",
                onRender: () => {
                    return <SearchBox placeholder={"Search ..."} onSearch={_onKeywordChanged} styles={{ root: { marginRight: 10 } }} />
                }
            });
        }

        if (!props.hideCreatePayslip && PermissionsService.hasPermission(['employees.payslips.create'], user.permissions)) {
            farItems.push({
                key: "register",
                text: "Create payslip",
                iconProps: { iconName: "Add" },
                onRender: () => {
                    return <NavLink to={'/hr/payslips/form'}>
                        <PrimaryButton text={"Create payslip"}
                            iconProps={{ iconName: "Add" }}
                            styles={{ root: { marginLeft: 10 } }} />
                    </NavLink>;
                }
            });
        }

        return { items, farItems };
    }

    const _onSurfaceDismissed = (refresh?: boolean) => {
        setActiveSurface(undefined);
        setActivePayslip(undefined);

        if (refresh) { _onRetrievePayslips() }
    }

    return <Stack className={styles.container} tokens={{ childrenGap: 20 }}>
        {!props.hideCommandBar ? <CommandBar
            items={getCommandBarItems().items}
            farItems={getCommandBarItems().farItems}
            styles={{
                root: {
                    padding: 0,
                    height: 'unset',
                    backgroundColor: 'transparent'
                }
            }}
            ariaLabel="Use left and right arrow keys to navigate between commands" /> : null}
        {selectedFilter.length > 0 ? <SelectedFilter selected={selectedFilter} onChange={(selected) => {
            setSelectedFilter(selected);
        }} /> : null}
        <Stack tokens={{ childrenGap: 10 }}>
            <Stack className={styles[props.variant || 'card']}>
                {pagination && payslips.length > 0 ? <Pagination goToPage={_onRetrievePayslips} currentPage={pagination.currentPage} totalPage={pagination.lastPage} text={pagination.total + " payslips found"} /> : null}
                {
                    !loaded || (loaded && payslips.length > 0) ? (
                        <>
                            <ShimmeredDetailsList
                                setKey="items"
                                items={payslips}
                                columns={columns.filter((col) => shownColumns.indexOf(col.key as TEmployeePayslipColumn) > -1)}
                                selectionMode={SelectionMode.none}
                                enableShimmer={!loaded}
                                onShouldVirtualize={() => false}
                                ariaLabelForShimmer="Content is being fetched"
                                ariaLabelForGrid="Item details" />
                        </>
                    ) : null
                }
                {loaded && payslips.length < 1 ? <Stack styles={{ root: { padding: 10 } }}>
                    <Text>Payslips not found</Text>
                </Stack> : null}
            </Stack>
            {pagination && payslips.length > 0 ? <Pagination goToPage={_onRetrievePayslips} currentPage={pagination.currentPage} totalPage={pagination.lastPage} /> : null}
        </Stack>
    </Stack>;
};

export default EmployeePayslipsList;
